import { ID, TaskLinkFields, TaskLinkIndex } from '@griply/types';

import { taskGoalIds, taskLifeAreaIds } from './task-link-ids';

// A bucket whose members did not change keeps its array identity, so a screen bound to
// one goal does not re-render when an unrelated task updates.
const reuseUnchanged = <T>(
  next: Map<ID, T[]>,
  previous?: Map<ID, T[]>,
): void => {
  if (!previous) {
    return;
  }

  next.forEach((items, key) => {
    const before = previous.get(key);
    if (
      before &&
      before.length === items.length &&
      before.every((item, index) => item === items[index])
    ) {
      next.set(key, before);
    }
  });
};

// One pass over the tasks instead of a filter per screen: every goal and life area a
// task links to gets it in its bucket.
export const buildTaskLinkIndex = <T extends TaskLinkFields & { id: ID }>(
  tasks: T[],
  previous?: TaskLinkIndex<T>,
): TaskLinkIndex<T> => {
  const byGoalId = new Map<ID, T[]>();
  const byLifeAreaId = new Map<ID, T[]>();

  const push = (bucket: Map<ID, T[]>, key: ID, task: T) => {
    const existing = bucket.get(key);
    if (existing) {
      existing.push(task);
      return;
    }
    bucket.set(key, [task]);
  };

  tasks.forEach((task) => {
    taskGoalIds(task).forEach((goalId) => push(byGoalId, goalId, task));
    taskLifeAreaIds(task).forEach((lifeAreaId) =>
      push(byLifeAreaId, lifeAreaId, task),
    );
  });

  reuseUnchanged(byGoalId, previous?.byGoalId);
  reuseUnchanged(byLifeAreaId, previous?.byLifeAreaId);

  return { byGoalId, byLifeAreaId };
};
